// src/utils/bookingCasePersistence.js
//
// SnoPro Accelerator F — the ONE shared, PURE projection of a booking case + its child bookings +
// audit into persistable document shapes and their DETERMINISTIC ids. Both the browser writer
// (`bookingCaseWriters.js`, firebase client SDK) and the Node/firebase-admin partner persistence
// adapter (`server/integrations/persistPartnerBooking.js`) import these helpers, so the two writers
// CANNOT drift on document identity or field projection.
//
// This module contains NO Firestore SDK calls and NO server timestamps — a caller stamps createdAt/
// updatedAt with its own SDK's serverTimestamp() after projecting the fields here. It is therefore
// server-pure (importable under plain Node) and browser-safe.

import { normalizeBooking } from './bookingDomain';
import { normalizePaymentState } from './paymentSemantics';

// ── DETERMINISTIC ids (the SAME ids the browser writer has always used) ─────────────
// A child booking's document id is its case + stable request-slot id. This is the permanent identity
// that makes create/retry/re-materialise idempotent — it MUST remain `${caseId}__${requestSlotId}`.
export function bookingIdForCaseSlot(caseId, requestSlotId) {
  return `${caseId}__${requestSlotId}`;
}

// The immutable case-audit event id, keyed by operationId + change type (create-once; never updated),
// so a replayed operation cannot duplicate the audit row.
export function caseAuditId(operationId, changeType) {
  return `${operationId}__${changeType}`;
}

// ── document projections (no timestamps — the writer adds createdAt/updatedAt) ──────
// The canonical BookingCase (parent enquiry) document field projection.
export function bookingCaseDocument(caseObj = {}) {
  return {
    caseModelVersion: 1, // explicit new-case marker
    organizationId: caseObj.organizationId, seasonId: caseObj.seasonId,
    // Pass 82C.2 §11 — persist the organization wall-clock timezone the request lines are expressed in, so
    // materialization (now and on later edits) resolves the correct instants for a non-JST tenant.
    timezone: caseObj.timezone || '',
    // Pass 82E — persist the case-level operating area (child bookings inherit it at materialization).
    operatingAreaId: caseObj.operatingAreaId || '',
    requesterName: caseObj.requesterName, requesterEmail: caseObj.requesterEmail, requesterPhone: caseObj.requesterPhone,
    // Pass 63 — clientId is the durable business identity; requesterUserId is the (optional) auth link.
    requesterUserId: caseObj.requesterUserId || '',
    clientId: caseObj.clientId || '', leadGuest: caseObj.leadGuest || null, additionalGuests: caseObj.additionalGuests || [],
    sourceType: caseObj.sourceType, sourceReference: caseObj.sourceReference, sourceThreadId: caseObj.sourceThreadId,
    sourceMessageIds: caseObj.sourceMessageIds, communications: caseObj.communications || [],
    // Accelerator D/F — durable back-reference to the originating SourceIntake (provenance, not identity).
    sourceIntakeId: caseObj.sourceIntakeId || '',
    stage: caseObj.stage,
    paymentState: normalizePaymentState(caseObj.paymentState), // canonical PAYMENT enum
    cancellationResolution: caseObj.cancellationResolution || '',
    invoiceNumber: caseObj.invoiceNumber, roombossNumber: caseObj.roombossNumber,
    priceSnapshot: caseObj.priceSnapshot, currency: caseObj.currency, referral: caseObj.referral,
    accommodationName: caseObj.accommodationName, accommodationAddress: caseObj.accommodationAddress,
    accommodationMapUrl: caseObj.accommodationMapUrl, tripNotes: caseObj.tripNotes,
    requestedSessions: caseObj.requestedSessions, status: caseObj.status || 'pending',
  };
}

// A child booking document from a materialised slot payload. Runs the ONE canonical normaliser so a
// case-materialised booking is byte-identical whether written by the browser or the admin adapter.
export function bookingDocumentForSlot(payload = {}, bookingId = '') {
  return normalizeBooking({ ...payload, id: bookingId });
}

// The immutable case-audit document (no timestamp — writer adds createdAt).
export function caseAuditDocument({ caseId = '', changeType = '', details = {}, actorUserId = '', actorName = '', operationId = '' } = {}) {
  return {
    caseId, changeType, details: details || {},
    changedByUserId: actorUserId || '', changedByName: actorName || '',
    operationId,
  };
}
